/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, Input, OnInit } from '@angular/core';
import { LineGraphicsDialogConfig } from './types';
import { getMaxContentWidth } from '../../../../../../../shared/ui/src/lib/utils/common.utils';

@Component({
  selector: 'sysque-line-graphics',
  templateUrl: './line-graphics.component.html',
  styleUrl: './line-graphics.component.scss',
  standalone: false
})
export class LineGraphicsComponent implements OnInit {
  @Input() config: LineGraphicsDialogConfig;

  public currentColor: string;
  public patterns: string[];
  public currentPattern: string;
  public currentLineWeight: number;
  public toggle = false;
  public calculatedWidth: string = 'auto'; // Dynamically calculated width

  /**
   * Initializes the component
   */
  public ngOnInit(): void {
    this.currentColor = this.rgbToHex(this.config.color);
    this.patterns = this.config.patterns;
    this.currentPattern = this.config.currentPattern;
    this.currentLineWeight = this.config.lineWeight;
    this.calculatedWidth = getMaxContentWidth(this.patterns); 
  }

  /**
   * Changes the color
   * @param color - the new color
   */
  public onColorChange(color: string): void {
    this.currentColor = color;
  }

  /**
   * Change all parameters
   */
  public applyChanges(): void {
    this.config.color = this.hexToRgb(this.currentColor);
    this.config.currentPattern = this.currentPattern;
    this.config.lineWeight = this.currentLineWeight;
    this.config.apply();
  }

  /**
   * Discard all changes
   */
  public discardChanges(): void {
    // Reset the values
    this.currentColor =this.rgbToHex(this.config.color);
    this.patterns = this.config.patterns;
    this.currentPattern = this.config.currentPattern;
    this.currentLineWeight = this.config.lineWeight;
  }

  /**
   * Change the pattern
   * @param pattern - the new pattern
   */
  public onPatternChange(pattern: string): void {
    this.currentPattern = pattern;
  }

  /**
   * Close the dialog
   */
  public closeDialog(): void {
    this.config.close();
  }

  /**
   * Converts the rgb color to hex
   * @param color - the rgb color
   * @returns - the hex color
   */
  private rgbToHex(color: string): string {
    // Define the rgb pattern
    const rgbPattern = /^(\d{1,3}),(\d{1,3}),(\d{1,3})$/;
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    // Check if the color is already in hex format
    if (hexPattern.test(color)) {
      return color;
    }

    // Check if the rgb color is valid
    if (!rgbPattern.test(color)) {
      return null;
    }

    // Convert the rgb color to hex
    let [r, g, b] = color.split(',').map(Number);
    r = Math.max(0, Math.min(255, r));
    g = Math.max(0, Math.min(255, g));
    b = Math.max(0, Math.min(255, b));

    // Return the hex color
    return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
  }

  /**
   * Converts the hex color to rgb
   * @param color - the hex color
   * @returns - the rgb color
   */
  private hexToRgb(color: string): string {
    // Define the hex pattern
    const rgbPattern = /^(\d{1,3}),(\d{1,3}),(\d{1,3})$/;
    const hexPattern = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

    // Check if the color is already in rgb format
    if (rgbPattern.test(color)) {
      return color;
    }

    // Check if the hex color is valid
    if (!hexPattern.test(color)) {
      return null;
    }

    // Convert the hex color to rgb
    const result = hexPattern.exec(color);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].join(',') : null;
  }
}
